import { Link } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, UserCircle, Heart, Sun, Moon, Gamepad2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { useTheme } from "@/context/ThemeContext";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnnouncementBar from "@/components/AnnouncementBar";

const categories = [
  { label: "All Gear", to: "/shop" },
  { label: "Brands", to: "/brands" },
  { label: "Peripherals", to: "/shop?cat=Peripherals" },
  { label: "Audio", to: "/shop?cat=Audio" },
  { label: "Setup Gear", to: "/shop?cat=Setup+Gear" },
  { label: "Gaming Merch", to: "/shop?cat=Gaming+Merch" },
  { label: "Collectibles", to: "/shop?cat=Collectibles" },
  { label: "Accessories", to: "/shop?cat=Accessories" },
  { label: "🔥 Today's Deals", to: "/shop?sort=price-low" },
];

const Navbar = () => {
  const { totalItems } = useCart();
  const { user } = useAuth();
  const { wishlistIds } = useWishlist();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");

  return (
    <>
      <AnnouncementBar />
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-primary/20 bg-background/95 backdrop-blur-xl">
        {/* Top Row */}
        <div className="container mx-auto flex h-14 items-center gap-4 px-4">
          <Link to="/" className="flex items-center gap-2 font-display text-xl font-black tracking-tight shrink-0">
            <Gamepad2 className="h-6 w-6 text-primary" />
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">GROVEFAN</span>
          </Link>

          {/* Search Bar */}
          <div className="relative hidden flex-1 max-w-2xl md:block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search mice, keyboards, headsets, merch..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && search.trim()) {
                  window.location.href = `/shop?q=${encodeURIComponent(search)}`;
                }
              }}
              className="w-full rounded-md border border-border bg-secondary py-2 pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              aria-label="Toggle theme"
              onClick={toggleTheme}
              className="rounded-md p-2 transition-colors hover:bg-secondary"
            >
              {theme === "dark" ? <Sun className="h-5 w-5 text-muted-foreground" /> : <Moon className="h-5 w-5 text-muted-foreground" />}
            </button>
            <Link to="/shop" aria-label="Search" className="rounded-md p-2 transition-colors hover:bg-secondary md:hidden">
              <Search className="h-5 w-5 text-muted-foreground" />
            </Link>
            <Link to="/wishlist" aria-label="Wishlist" className="relative rounded-md p-2 transition-colors hover:bg-secondary">
              <Heart className={`h-5 w-5 ${wishlistIds.length > 0 ? "text-glow-red fill-current" : "text-muted-foreground"}`} />
              {wishlistIds.length > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-sm bg-destructive text-[9px] font-bold text-destructive-foreground">
                  {wishlistIds.length}
                </span>
              )}
            </Link>
            <Link to="/cart" aria-label="Cart" className="relative rounded-md p-2 transition-colors hover:bg-secondary">
              <ShoppingBag className="h-5 w-5 text-muted-foreground" />
              {totalItems > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-sm bg-primary text-[9px] font-bold text-primary-foreground">
                  {totalItems}
                </span>
              )}
            </Link>
            <Link
              to={user ? "/account" : "/login"}
              aria-label={user ? "Account" : "Sign in"}
              className="rounded-md p-2 transition-colors hover:bg-secondary"
            >
              <UserCircle className={`h-5 w-5 ${user ? "text-primary" : "text-muted-foreground"}`} />
            </Link>
            <button aria-label="Toggle menu" className="rounded-md p-2 transition-colors hover:bg-secondary md:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Category Strip — Desktop */}
        <div className="hidden border-t border-border/50 bg-secondary/50 md:block overflow-hidden">
          <div className="container mx-auto flex items-center gap-0 px-4 overflow-x-auto scrollbar-hide">
            {categories.map((cat) => (
              <Link
                key={cat.label}
                to={cat.to}
                className="whitespace-nowrap px-4 py-2 text-xs font-medium text-muted-foreground transition-colors hover:text-primary hover:bg-primary/5"
              >
                {cat.label}
              </Link>
            ))}
            <Link
              to="/sell"
              className="whitespace-nowrap px-4 py-2 text-xs font-bold text-primary transition-colors hover:text-accent hover:bg-primary/5"
            >
              🚀 Start Selling
            </Link>
            <Link
              to="/affiliate"
              className="whitespace-nowrap px-4 py-2 text-xs font-bold text-primary transition-colors hover:text-accent hover:bg-primary/5"
            >
              💰 Affiliate
            </Link>
          </div>
        </div>

        {/* RGB underglow line */}
        <div className="h-[2px] gradient-rgb" />

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-t border-border/50 bg-background md:hidden"
            >
              <div className="flex flex-col gap-1 p-4">
                {/* Mobile search */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full rounded-md border border-border bg-secondary py-2.5 pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                  />
                </div>
                <p className="px-2 text-[10px] font-bold uppercase tracking-widest text-primary">Categories</p>
                {categories.map((cat) => (
                  <Link
                    key={cat.label}
                    to={cat.to}
                    onClick={() => setMobileOpen(false)}
                    className="rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary"
                  >
                    {cat.label}
                  </Link>
                ))}
                <div className="my-2 h-px bg-border" />
                <Link to="/" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2 text-sm font-medium hover:bg-secondary">Home</Link>
                <Link to="/about" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2 text-sm font-medium hover:bg-secondary">About</Link>
                <Link to="/contact" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2 text-sm font-medium hover:bg-secondary">Contact</Link>
                <Link to="/sell" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2 text-sm font-bold text-primary hover:bg-secondary">🚀 Start Selling</Link>
                <Link to="/affiliate" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2 text-sm font-bold text-primary hover:bg-secondary">💰 Affiliate</Link>
                <Link
                  to={user ? "/account" : "/login"}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md px-3 py-2 text-sm font-medium hover:bg-secondary"
                >
                  {user ? "My Account" : "Sign In"}
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;
